import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { money } from "./CommissionList";

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const ENTRY_STYLE = { not_due: "bg-slate-100 text-slate-500", due: "bg-amber-50 text-amber-700", paid: "bg-emerald-50 text-emerald-700" };

export default function CommissionDetail() {
  const { commissionId } = useParams();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const base = isAdmin ? "/app/admin" : "/app/partner";
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fees, setFees] = useState({ gross_tuition: "", scholarship: "", discount: "" });

  const load = useCallback(() => api.get(`/commissions/${commissionId}`).then((r) => setRecord(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Commission not found")), [commissionId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      await api.post(`/commissions/${commissionId}/${path}`, body);
      if (message) toast.success(message);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="rounded-2xl bg-rose-50 p-4 text-sm font-semibold text-rose-700" data-testid="commission-error">{error}</p>;
  if (!record) return <p className="text-sm text-slatey">Loading…</p>;

  const calc = record.calculation || {};
  const conditions = record.required_conditions || [];

  return (
    <div data-testid="commission-detail" className="space-y-6">
      <Link to={`${base}/commissions`} className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> All commissions</Link>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink" data-testid="commission-learner">
              {record.learner_name} {record.is_demo && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">TEST</span>}
            </h1>
            <p className="mt-1 text-sm text-slatey">{record.opportunity_title} · {record.provider_name} · {record.intake_label}</p>
            <p className="mt-1 text-xs text-slatey">{record.application_reference} · {record.partner_name} · config v{record.config_version}</p>
          </div>
          <span className="rounded-full bg-cobalt/10 px-4 py-2 text-sm font-bold capitalize text-cobalt" data-testid="commission-status">{record.status}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          {/* Auditable calculation breakdown */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="commission-breakdown">
            <h2 className="font-display text-xl font-bold text-ink">Calculation breakdown</h2>
            {calc.total != null || calc.excluded ? (
              <dl className="mt-3 space-y-2 text-sm">
                {[["University", calc.university], ["Intake", calc.intake],
                  ["Students in intake", calc.students_in_intake],
                  ["Applicable tier", calc.tier_applied ? `${calc.tier_applied.min_students}–${calc.tier_applied.max_students ?? "∞"} students → ${calc.percentage}%` : (calc.percentage != null ? `${calc.percentage}%` : "—")],
                  ["Gross tuition", money(calc.gross_tuition, calc.currency)],
                  ["Scholarship", `− ${money(calc.scholarship, calc.currency)}`],
                  ["Discount", `− ${money(calc.discount, calc.currency)}`],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4"><dt className="text-slatey">{label}</dt><dd className="font-semibold text-ink" data-testid={`breakdown-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</dd></div>
                ))}
                <div className="flex justify-between gap-4 border-t border-slate-100 pt-2"><dt className="text-slatey">Year 1 net tuition</dt><dd className="font-bold text-ink" data-testid="breakdown-net-tuition">{money(calc.net_tuition_year_1, calc.currency)}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slatey">Calculated commission</dt><dd className="font-display text-lg font-bold text-cobalt" data-testid="breakdown-total">{money(calc.total, calc.currency)}</dd></div>
                {calc.basis && <p className="text-xs text-slatey" data-testid="breakdown-basis">Basis: {calc.basis}{calc.vat_inclusive ? " · VAT inclusive" : ""}{calc.cap ? ` · ${calc.cap}` : ""}</p>}
                {calc.excluded && <p className="rounded-2xl bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="breakdown-exclusion">{calc.exclusion_reason}</p>}
                {calc.exclusions_checked?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-bold uppercase text-slatey">Exclusions evaluated</p>
                    <ul className="mt-1 space-y-1">
                      {calc.exclusions_checked.map((x, i) => (
                        <li key={i} className="text-xs text-slatey" data-testid={`exclusion-check-${i}`}>
                          <span className={`font-bold ${x.matched ? "text-rose-600" : "text-emerald-600"}`}>{x.matched ? "Matched" : "Not matched"}</span> — {x.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </dl>
            ) : <p className="mt-3 text-sm text-slatey">No calculation yet — record enrolment to calculate.</p>}
          </section>

          {/* Payment schedule */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="commission-schedule">
            <h2 className="font-display text-xl font-bold text-ink">Payment schedule</h2>
            {record.payment_schedule?.length ? (
              <ul className="mt-3 space-y-2">
                {record.payment_schedule.map((entry, i) => (
                  <li key={entry.label || i} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`schedule-entry-${i}`}>
                    <span className="font-semibold text-ink">{entry.label} <span className="text-xs text-slatey">({entry.percentage}%)</span></span>
                    <span className="flex items-center gap-3">
                      <span className="font-bold text-ink">{money(entry.amount, calc.currency)}</span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${ENTRY_STYLE[entry.status]}`} data-testid={`schedule-status-${i}`}>{entry.status.replace("_", " ")}</span>
                      {isAdmin && entry.status === "due" && (
                        <button onClick={() => act("payments", { label: entry.label }, `${entry.label} marked paid.`)} disabled={busy} data-testid={`schedule-pay-${i}`}
                          className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Mark paid</button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-sm text-slatey">No schedule yet.</p>}
          </section>

          {/* Audit events */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="commission-events">
            <h2 className="font-display text-xl font-bold text-ink">Audit trail</h2>
            <ol className="mt-3 space-y-3">
              {(record.events || []).slice().reverse().map((event, index) => (
                <li key={index} className="flex gap-3 text-sm" data-testid={`commission-event-${index}`}>
                  <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-cobalt" />
                  <div>
                    <p className="font-semibold capitalize text-ink">{event.action.replaceAll("_", " ")}</p>
                    {event.detail && <p className="text-xs leading-5 text-slatey">{event.detail}</p>}
                    <p className="text-xs text-slatey">{event.by} · {event.at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="commission-actions">
            <h2 className="font-display text-xl font-bold text-ink">Actions</h2>

            {record.status === "potential" && (
              <div className="mt-3">
                <p className="text-xs font-bold uppercase text-slatey">Record enrolment</p>
                {[["gross_tuition", "Gross tuition (year 1)"], ["scholarship", "Scholarship"], ["discount", "Discount"]].map(([key, placeholder]) => (
                  <input key={key} type="number" min="0" value={fees[key]} onChange={(e) => setFees((f) => ({ ...f, [key]: e.target.value }))}
                    className={`${input} mt-2`} placeholder={placeholder} data-testid={`enrolment-${key}`} />
                ))}
                <button onClick={() => act("enrolment", { gross_tuition: Number(fees.gross_tuition || 0), scholarship: Number(fees.scholarship || 0), discount: Number(fees.discount || 0) }, "Enrolment recorded and commission calculated.")}
                  disabled={busy || !fees.gross_tuition} data-testid="enrolment-submit"
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record enrolment
                </button>
              </div>
            )}

            {["pending", "confirmed"].includes(record.status) && conditions.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase text-slatey">Qualifying conditions</p>
                {record.qualifying_conditions_text && <p className="mt-1 text-xs leading-5 text-slatey">{record.qualifying_conditions_text}</p>}
                <ul className="mt-2 space-y-2">
                  {conditions.map((condition) => (
                    <li key={condition.key} className="flex items-center justify-between gap-2 rounded-2xl bg-surface px-3 py-2 text-xs" data-testid={`condition-${condition.key}`}>
                      <span className="font-semibold capitalize text-ink">{condition.key.replaceAll("_", " ")}</span>
                      <button onClick={() => act(`conditions/${condition.key}`, { satisfied: !condition.satisfied })}
                        disabled={busy} data-testid={`condition-toggle-${condition.key}`}
                        className={`rounded-full px-3 py-1.5 font-bold disabled:opacity-50 ${condition.satisfied ? "bg-emerald-100 text-emerald-700" : "border border-slate-300 text-slatey"}`}>
                        {condition.satisfied ? "Satisfied" : "Mark satisfied"}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isAdmin && record.status === "confirmed" && (
              <button onClick={() => act("approve", {}, "Commission approved.")} disabled={busy} data-testid="commission-approve"
                className="mt-4 w-full rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Approve commission</button>
            )}
            {isAdmin && record.status === "approved" && (
              <button onClick={() => act("payable", {}, "Payment schedule activated.")} disabled={busy} data-testid="commission-payable"
                className="mt-4 w-full rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Mark payable</button>
            )}
            {isAdmin && ["pending", "confirmed", "approved", "payable", "excluded"].includes(record.status) && (
              <button onClick={() => { if (window.confirm("Recalculate using the CURRENT partner configuration? This is an authorised admin action and is audited.")) act("recalculate", { confirm: true }, "Recalculated against current configuration."); }}
                disabled={busy} data-testid="commission-recalculate"
                className="mt-3 w-full rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink disabled:opacity-50">Recalculate (current config)</button>
            )}
            {isAdmin && !["paid", "void"].includes(record.status) && (
              <button onClick={() => { const reason = window.prompt("Reason for voiding this commission?"); if (reason?.trim().length >= 3) act("void", { reason }, "Commission voided."); }}
                disabled={busy} data-testid="commission-void"
                className="mt-3 w-full rounded-full border border-rose-200 px-5 py-3 text-sm font-bold text-rose-600 disabled:opacity-50">Void commission</button>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-xl font-bold text-ink">Rule</h2>
            <p className="mt-2 text-sm font-semibold text-ink" data-testid="commission-rule-label">{record.rule_label}</p>
            <p className="mt-1 text-xs text-slatey">Qualifying event: {(record.qualifying_event || "").replaceAll("_", " ")}</p>
            <Link to={`${base === "/app/admin" ? "/app/partner" : base}/applications/${record.application_id}`} data-testid="commission-application-link"
              className="mt-3 inline-block text-sm font-bold text-cobalt hover:underline">View application</Link>
          </section>
        </aside>
      </div>
    </div>
  );
}
